import {
  allKeyCategories,
  allKeyTypes,
  AppKey,
  AppKeyCategory,
  AppKeyCreate,
  AppKeyImport,
  Issuer,
  IssuerCreate,
  ResourceFetchParams
} from "../types";

export const waitOut: (timeout: number) => Promise<void> = timeout =>
  new Promise(resolve => {
    setTimeout(resolve, timeout);
  });

const randomNames = [
  "Philips",
  "Cubana",
  "James",
  "Dextco",
  "Crayons",
  "Cola Kola",
  "Damina",
  "Wimbledon",
  "Jumonia",
  "Kripke",
  "Nakamura",
  "Ding Liren",
  "Noob Saibot",
  "Tchaivosky",
  "All hallows",
  "Fidelity",
  "Zenith",
  "Guarranty Trust",
  "Access"
];

const randomNameExts = [
  "Inc",
  "Limited",
  "Corp",
  "Org",
  "Industries",
  "Holdings",
  "Bank",
  "Hospital",
  "Tech",
  "and Sons",
  ""
];

export class MockIssuerService {
  private static issuersCount = Math.floor(Math.random() * 50) + 30;

  private static allIssuers: Issuer[] = [];

  public static initializeDatasets(): void {
    this.allIssuers = Array(this.issuersCount)
      .fill("")
      .map((_, index) => {
        const name = `${
          randomNames[Math.floor(Math.random() * randomNames.length)]
        } ${randomNameExts[Math.floor(Math.random() * randomNameExts.length)]}`;
        return {
          id: index,
          name,
          description: "Some rando description",
          identifiers: name.split(" "),
          isManufacturer: Math.random() > 0.5,
          issuerCode: name.slice(0, 3).toUpperCase()
        };
      });
  }

  private static getRandomKeyData(parentIssuer: Issuer, key?: number): AppKey {
    const keyCategory: AppKeyCategory =
      allKeyCategories[Math.floor(Math.random() * allKeyCategories.length)];
    const keyVersion = Math.random() > 0.4 ? "01" : "02";
    const envFlag = Math.random() > 0.6 ? "LIVE" : "TEST";
    return {
      id: key || parentIssuer.keys?.length || 0,
      issuerId: parentIssuer.id,
      keyType: allKeyTypes[Math.floor(Math.random() * allKeyTypes.length)],
      issuerBin: keyCategory === "RSA" ? "43904" : undefined,
      keyExponentValue:
        keyCategory === "RSA" ? Math.floor(Math.random() * 16 + 4) : undefined,
      keyValue: null,
      keyCategory,
      keyIndex: Math.random() > 0.4 ? "01" : "02",
      activated: Math.random() > 0.3,
      keyVersion,
      envFlag,
      keyLength: [8, 16, 24, 32, 64][Math.floor(Math.random() * 5)],
      expiryDate: `${String(Math.floor(Math.random() * 12)).padStart(2, "0")}${
        Math.random() > 0.5 ? "24" : "23"
      }`,
      keyDescription: "Some rando desc",
      keyLabel: `${keyCategory}.${envFlag}.${keyVersion}`,
      hasKey: false
    };
  }

  public static async createIssuer(issuer: IssuerCreate): Promise<void> {
    await waitOut(1000);
    this.allIssuers = [
      { ...issuer, id: this.issuersCount, keys: [] },
      ...this.allIssuers
    ];
    this.issuersCount += 1;
  }

  public static async updateIssuer(issuer: Issuer): Promise<void> {
    await waitOut(1000);
    this.allIssuers = this.allIssuers.map(_issuer =>
      _issuer.id === issuer.id ? { ...issuer } : _issuer
    );
  }

  public static async getIssuerKeys(issuerCode: string): Promise<AppKey[]> {
    await waitOut(600);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    if (issuer.keys) {
      return issuer.keys;
    }
    issuer.keys = Array(Math.floor(Math.random() * 16) + 8)
      .fill("")
      .map((_, i) => this.getRandomKeyData(issuer, i))
      .sort((a, b) => b.keyCategory?.localeCompare(a.keyCategory || "") || 0);

    return issuer.keys;
  }

  public static async createIssuerKey(
    key: AppKeyCreate,
    issuerCode: string
  ): Promise<void> {
    await waitOut(1000);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    issuer.keys = [
      { ...this.getRandomKeyData(issuer), ...key },
      ...(issuer.keys || [])
    ];
  }

  public static async updateIssuerKey(
    key: AppKey,
    issuerCode: string
  ): Promise<void> {
    await waitOut(1000);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    issuer.keys =
      issuer.keys?.map(_key =>
        _key.id === key.id ? { ..._key, ...key } : _key
      ) || [];
  }

  public static async autoGenerateKeyValue(
    key: AppKey,
    issuerCode: string
  ): Promise<void> {
    await waitOut(1000);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    issuer.keys =
      issuer.keys?.map(_key =>
        _key.id === key.id ? { ..._key, keyValue: "rando value" } : _key
      ) || [];
  }

  public static async deleteIssuerKey(
    key: AppKey,
    issuerCode: string
  ): Promise<void> {
    await waitOut(1000);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    issuer.keys = issuer.keys?.filter(_key => _key.id !== key.id) || [];
  }

  public static async importIssuerKeyValue(
    payload: AppKeyImport,
    key: AppKey,
    issuerCode: string
  ): Promise<void> {
    await waitOut(1000);
    const issuer = this.allIssuers.find(
      issuer => issuer.issuerCode === issuerCode
    );
    if (!issuer) {
      throw Error("Invalid Issuer code");
    }
    issuer.keys =
      issuer.keys?.map(_key =>
        _key.id === key.id ? { ..._key, keyValue: payload.keyValue } : _key
      ) || [];
  }

  public static async getRandomIssuers({
    pageNumber,
    pageSize,
    searchStr
  }: ResourceFetchParams): Promise<{ data: Issuer[]; count: number }> {
    await waitOut(600);
    const regex = new RegExp(searchStr || "", "i");
    const hits = searchStr
      ? this.allIssuers.filter(record => regex.test(record.name))
      : this.allIssuers;
    return {
      data: hits.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
      count: hits.length
    };
  }

  public static async downloadCertificate(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    key: AppKey,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    issuerCode: string
  ): Promise<any> {
    await waitOut(1000);
    return {
      mac: "130F77F3",
      issuerPublicKey:
        "3081FC0281F6955CCE0F4686B5517ED312D6C2DC2BF63A882F52A01BAD48E1FAFE6878765F3C40E7B166E4581EBC3016AE0ECEDF0A45B157CA60DD6E6A630265558063A8DCA73183D7B429AC2ED20423151288935EE079D1870A86037BAA024C9B000476D211B79F40750E43331028DEBA29D6EB315836735A54CA973B4C45B53407689F3AF52C57B9D2021CFBACB09ADB8BC694402113FAD5FE4E3A54BF27421393707EC13586F2F42E492EA50CF7B88AB097DE3D29E3C34733CAE8685E185AFA52CD8152459CFD8A615FE7B927E19F368F7F1A14F3EA21DA71F5BFF2D393F6D5CBFEFFD9E48374BD3602D63D1E85442F3D1A6D39B08FB56239416B020103",
      issuerPrivateKeyLength: "0624",
      issuerPrivateKey:
        "6AF54BC3386672BF6343E2AFE0B101B9423B838A359C7F7056C01A7E2B23BFA5D09272CF17FEF2E64265096BEC2641B5174871E4E6153622A68FD54ADA83BF607260A2DDA368157B9D8C6859C1EA19166D9FEE4020D08DA90D03815743DB421FEF5DE13A8CED903BF23F0389C7D345782ADABADAA964E5267756B395981B2D5515EA18BAC4AAFDE9B50792B85895C526CE8FFEA66541E53994AA1337338DCB2FA58D7E2F16BFA7AF72D195BAC442A88F4AB09D39A474DC9971DCA0EB1A61A82156F974715A3C09AD3478C08C3C5D09F7B021BB73CCF5356DE2683F274D7B31CDBF6DEFA62D0BBF84B9635F7EC2C29E71FC7D766216F918890C74880D06922E9BCE0F712BC93236EF9AD60D31DACAABD081734E54023383317C6658E10DE6C44AED177774DB63F71CE725C00BB90262F42C0DA13AFC0AAD9842EF309F114AF802DF07488FA42CDFA4D9194E5DC26872253A6204A1632C2752FBA072EC3825C578CBA725C871012A263A0D5919E2E297EFEB2CED29CDBB52CE47C18527E1F843B08C56FE4C99E112B645D494E7008D0E6532F087F47912CFA6FDDFA4C20085EC2D91701767E269271B59B6B9371CB06164A2F17B96B9DF055E433621CD85E371115EEFEF79802D73EB236E22F413A3915E6FFF345B71F397F502D11A7C8474BE746EF517099C30E3DBC5FA7D6C0D7945C6AF72D049335439F370BF3F5BCAB0CAABDB12FA49EF0290F4AF2D0AD827852DED939EE1731032DD5D775384403ACF365509F72E4D7C957F2E1B35AE243AFE1E2F4F181DFA40D7B71DD45A91380184287FDF78BC7C0AB800087EFFA6C7E3DE2276355B31CDCA96133C47E9C9FE300DF1B98D7AD92A15F7288436E64626EE73D5AB",
      certificateLength: "0503",
      selfSignedIssuerCertificate:
        "506999FF1643AD01F601955CCE0F4686B5517ED312D6C2DC2BF63A882F52A01BAD48E1FAFE6878765F3C40E7B166E4581EBC3016AE0ECEDF0A45B157CA60DD6E6A630265558063A8DCA73183D7B429AC2ED20423151288935EE079D1870A86037BAA024C9B000476D211B79F40750E43331028DEBA29D6EB315836735A54CA973B4C45B53407689F3AF52C57B9D2021CFBACB09ADB8BC694402113FAD5FE4E3A54BF27421393707EC13586F2F42E492EA50CF7B88AB097DE3D29E3C34733CAE8685E185AFA52CD8152459CFD8A615FE7B927E19F368F7F1A14F3EA21DA71F5BFF2D393F6D5CBFEFFD9E48374BD3602D63D1E85442F3D1A6D39B08FB56239416B034472DAB4E2E5E68285FF471BD403EA7DAC4E3B7333FCB9411DFF9F15D2812A15FE1E1F7215182843AD28F66F1C9F26C2AAFCEC9AA2E1A97EB8184E5A812A16AA9F77647527F2D41FED45E03D0C79971A588A91BA5883BA11CD2115FD3109A0911C015C8209F570B8A4CFC005E4574C7DF29A3CAF8ED7E0AB5E43035F26C1A5D1A7E290EC7176862007D68B40626D6788C7A2717B35F9162EFC443FE473B5854AD557F954DBFD02D3467DDDAB09C9190C5D0B5FB530C36465C5AF063FC376944440CBB187F9550C388AA4D6355C415EA5DF9402312BC4439D871E43A3B3DE0179EBD98965C3A90438C885EEB50779172397170EFE2A08",
      hashLength: "40",
      hash: "FAFD1D023A39B4DB44F780EC024A237C95365447",
      issuerPubKeyModulus: null,
      publicKeyExponent: null
    };
  }
}

MockIssuerService.initializeDatasets();
